import * as React from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { execCmd, makeRangeKeeper } from "../mail/richtext/editorCommands";
import { NOTIZBILD_MIME, externesBild } from "./bildDarstellung";
import { einsetzenAnAuswahl, linkEinsetzen, urlAusText } from "./einfuegen";
import {
    BLOCK_FARBEN,
    BLOCK_GROESSEN,
    BLOCK_MARKER,
    BLOCK_SCHRIFTEN,
    GRUPPE_FARBE,
    GRUPPE_GROESSE,
    GRUPPE_MARKER,
    GRUPPE_SCHRIFT,
    entferneFormatierung,
    setzeKlasse,
} from "./blockFormat";
import { kannRueckgaengig, mitVerlauf, rueckgaengig } from "./formatVerlauf";
import "./notizbloecke.css";
import { hinweisen } from "../redesign/bestaetigen";
import { alleDokumente, alleFenster, aktiveAuswahl, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * Das Rechtsklick-Menü an einem Notizblock — EIN Menü mit zwei Teilen
 * (Design-Stand 08.09.2026): oben die schwebende Formatierungsleiste nach
 * OneNote-Vorbild, darunter die Block-Aktionen.
 *
 * ══ WARUM NICHT `Vg3Kontextmenue` ══════════════════════════════════════════
 * Jenes Menü kennt nur Textzeilen (`MenuePunkt` = Label + Klick) — für eine
 * Knopfleiste ist dort kein Platz. Umbauen ließ es sich nicht: das
 * Kundentagebuch hängt daran und soll sich nicht verändern. Die
 * Viewport-Klemmung und die Schließer sind von dort ÜBERNOMMEN, nicht neu
 * erfunden; die Optik folgt denselben Tokens.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ DIE AUSWAHL DARF NICHT VERLOREN GEHEN ══════════════════════════════════
 * Ein Rechtsklick allein löscht die Markierung nicht — der Klick INS Menü
 * täte es. Zwei Vorkehrungen, beide aus `RichTextEditor` übernommen:
 *  · Knöpfe verhindern ihr `mousedown`-Default, damit der Fokus im Textfeld
 *    bleibt.
 *  · Ein `<select>` darf das NICHT (sonst öffnet es sich nicht). Dort wird die
 *    Auswahl beim `mousedown` gemerkt und vor dem Kommando wiederhergestellt
 *    (`makeRangeKeeper`).
 * Gemerkt wird zusätzlich beim Öffnen des Menüs — der früheste sichere Punkt.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bilder und Links (PO 14.09.2026): „Link…" und „Bild-URL…" öffnen unten in
 * der Leiste eine Adresszeile; „Bild…" den Dateidialog. Das Einsetzen selbst
 * steht in `einfuegen.ts`, der Upload beim Block (`onBildDatei`) — das Menü
 * kennt weder Vorgang noch Dokumente.
 */

/** Eine Zeile im unteren Teil. Formgleich mit `MenuePunkt` aus V3. */
export type BlockAktion = {
    label: string;
    onClick: () => void;
    /** Rot einfärben (löschende Aktionen). */
    gefahr?: boolean;
    icon?: React.ReactNode;
    /** Trennlinie ÜBER diesem Punkt. */
    trennerDavor?: boolean;
};

const RAND = 8;
/** Wert der Platzhalter-Option; die Auswahlliste ist eine Aktion, kein Zustand. */
const PLATZHALTER = " ";

/**
 * Aufzählung bzw. Nummerierung als echtes SVG (Optik-Regel der Guideline:
 * Icons als SVG, nicht als Unicode-Zeichen — Unicode-Glyphen fallen je nach
 * Schrift unterschiedlich aus).
 */
const ListenZeichen: React.FC<{ nummeriert?: boolean }> = ({ nummeriert }) => (
    <svg
        width={14}
        height={14}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        aria-hidden
    >
        <path d="M6 4h8M6 8h8M6 12h8" />
        {nummeriert ? (
            <>
                <path d="M2 2.6 3 2v3" />
                <path d="M2 7h2l-2 2.4h2" />
                <path d="M2 11h2l-1 1.2h.6a.9.9 0 1 1-.6 1.6" />
            </>
        ) : (
            <>
                <circle cx={2.6} cy={4} r={1} fill="currentColor" stroke="none" />
                <circle cx={2.6} cy={8} r={1} fill="currentColor" stroke="none" />
                <circle cx={2.6} cy={12} r={1} fill="currentColor" stroke="none" />
            </>
        )}
    </svg>
);

/** „Nichts davon" — durchgestrichener Kreis für „kein Marker"/„Standardfarbe". */
const OhneZeichen: React.FC = () => (
    <svg
        width={13}
        height={13}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        aria-hidden
    >
        <circle cx={8} cy={8} r={6} />
        <path d="M3.8 12.2 12.2 3.8" />
    </svg>
);

/** Pfeil zurück — „Rückgängig". */
const ZurueckZeichen: React.FC = () => (
    <svg
        width={14}
        height={14}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M5.5 3 2.5 6l3 3" />
        <path d="M2.5 6h7a4 4 0 0 1 0 8H7" />
    </svg>
);

/** Ein „T" mit Schrägstrich — „Formatierung entfernen" wie in Word/Outlook. */
const OhneFormatZeichen: React.FC = () => (
    <svg
        width={14}
        height={14}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        aria-hidden
    >
        <path d="M3 3h8M7 3 5 13" />
        <path d="m9.5 9.5 4 4M13.5 9.5l-4 4" />
    </svg>
);

/** Kettenglied — der Link. */
const LinkZeichen: React.FC = () => (
    <svg
        width={14}
        height={14}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        aria-hidden
    >
        <path d="M6.5 9.5 9.5 6.5" />
        <path d="M7 4.5 8.3 3.2a2.6 2.6 0 0 1 3.7 3.7L10.7 8.2" />
        <path d="M9 11.5 7.7 12.8a2.6 2.6 0 0 1-3.7-3.7L5.3 7.8" />
    </svg>
);

/** Bild — ein Rahmen mit Berg und Sonne. */
const BildZeichen: React.FC = () => (
    <svg
        width={14}
        height={14}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinejoin="round"
        aria-hidden
    >
        <rect x={2} y={3} width={12} height={10} rx={1.5} />
        <path d="m3 12 3.5-4 2.5 3 1.5-1.5L13 12" />
        <circle cx={10.5} cy={6} r={1} fill="currentColor" stroke="none" />
    </svg>
);

/** Bild aus dem Netz — derselbe Rahmen mit einem Globus-Bogen. */
const BildUrlZeichen: React.FC = () => (
    <svg
        width={14}
        height={14}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinejoin="round"
        aria-hidden
    >
        <rect x={2} y={3} width={9} height={8} rx={1.5} />
        <path d="m3 10 2.5-3 2 2.2L8.5 8 10 10" />
        <circle cx={12} cy={11.5} r={2.8} />
        <path d="M9.2 11.5h5.6M12 8.7c1.2 1.2 1.2 4.4 0 5.6M12 8.7c-1.2 1.2-1.2 4.4 0 5.6" />
    </svg>
);

/**
 * Ein Knopf der Leiste.
 *
 * `mousedown` verhindert sein Default — nur so bleibt der Fokus (und damit die
 * Markierung) im Textfeld, während man die Leiste bedient. AUSSERHALB der
 * Menü-Komponente, damit die Knöpfe bei einem Neurendern nicht neu montiert
 * werden: ein Wechsel des DOM-Knotens zwischen `mousedown` und `mouseup`
 * verschluckte den Klick.
 */
const Knopf: React.FC<{
    titel: string;
    onClick: () => void;
    children: React.ReactNode;
    stil?: React.CSSProperties;
    gesperrt?: boolean;
}> = ({ titel, onClick, children, stil, gesperrt }) => (
    <button
        type="button"
        className="nb-fmt-knopf"
        title={titel}
        aria-label={titel}
        disabled={gesperrt}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        {...(stil ? { style: stil } : {})}
    >
        {children}
    </button>
);

export const BlockKontextmenue: React.FC<{
    x: number;
    y: number;
    /**
     * Das `contentEditable`, auf das die Formatierung wirkt. Fehlt es, zeigt
     * das Menü nur die Aktionen — etwa an einem fremden Block, in dem man gar
     * nicht schreiben darf.
     */
    feld: HTMLElement | null;
    aktionen: BlockAktion[];
    onClose: () => void;
    /**
     * Es wurde formatiert. Der Block speichert nach kurzer Ruhe und muss
     * angestoßen werden: `execCommand` löst zwar ein `input` aus, das
     * Umschreiben der Marker in Klassen-Hüllen danach aber nicht mehr.
     */
    onFormatiert?: () => void;
    /**
     * Eine Bilddatei einfügen (PO 14.09.2026). Der Block lädt hoch und setzt
     * den Marker an der Auswahl ein — das Menü stellt vorher nur die
     * Auswahl wieder her, die der Dateidialog gekostet hat. Fehlt der
     * Handler, gibt es den Knopf „Bild…" nicht.
     */
    onBildDatei?: (file: File) => void;
}> = ({ x, y, feld, aktionen, onClose, onFormatiert, onBildDatei }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const dateiRef = useRef<HTMLInputElement | null>(null);
    const [pos, setPos] = useState({ top: y, left: x });
    const keeper = useMemo(() => makeRangeKeeper(() => feld), [feld]);
    /**
     * Die Adresszeile für Link bzw. Bild-URL; null = zu. Ein `<input>` im
     * Menü nimmt dem Feld den Fokus wie ein `<select>` — die Auswahl ist
     * beim Öffnen der Zeile gemerkt und wird vor dem Einsetzen zurückgeholt.
     */
    const [adresse, setAdresse] = useState<{ modus: "link" | "bild"; wert: string } | null>(null);
    /** Neu zeichnen nach jedem Schritt — der Rückgängig-Knopf fragt das Feld. */
    const [, setStand] = useState(0);

    // Beim Öffnen merken — danach kann ein Klick ins Menü die Auswahl kosten.
    useEffect(() => {
        keeper.save();
    }, [keeper]);

    // Erst messen, dann in den Viewport klemmen: sonst läuft das Menü am
    // unteren oder rechten Rand aus dem Bild (übernommen aus `Vg3Kontextmenue`).
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setPos({
            top: Math.max(RAND, Math.min(y, aktivesFenster().innerHeight - r.height - RAND)),
            left: Math.max(RAND, Math.min(x, aktivesFenster().innerWidth - r.width - RAND)),
        });
    }, [x, y]);

    useEffect(() => {
        const runter = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) onClose();
        };
        const taste = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        // `capture`, damit auch ein Klick in einen Portal-Nachbarn schließt.
        alleDokumente.addEventListener("pointerdown", runter, true);
        alleDokumente.addEventListener("keydown", taste);
        alleFenster.addEventListener("resize", onClose);
        alleFenster.addEventListener("scroll", onClose, true);
        return () => {
            alleDokumente.removeEventListener("pointerdown", runter, true);
            alleDokumente.removeEventListener("keydown", taste);
            alleFenster.removeEventListener("resize", onClose);
            alleFenster.removeEventListener("scroll", onClose, true);
        };
    }, [onClose]);

    /**
     * Ein Kommando ausführen. Erst die gemerkte Auswahl zurückholen, dann
     * arbeiten, dann das Speichern anstoßen — das Menü bleibt offen, wie die
     * schwebende Leiste in OneNote.
     */
    const kommando = (fn: () => void) => {
        if (!feld) return;
        keeper.restore();
        // Jeder Schritt landet im Verlauf — „Rückgängig" (T-282).
        mitVerlauf(feld, fn);
        keeper.save();
        setStand((n) => n + 1);
        onFormatiert?.();
    };

    /** Den letzten Schritt der Palette zurücknehmen (T-282, „Zurück"-Knopf). */
    const zurueck = () => {
        if (!feld || !rueckgaengig(feld)) return;
        keeper.save();
        setStand((n) => n + 1);
        onFormatiert?.();
    };

    const klasse = (wert: string | null, gruppe: string, tag: "span" | "mark" = "span") =>
        kommando(() => setzeKlasse(feld, wert, gruppe, tag));

    /** Adresszeile öffnen — die Auswahl vorher merken, der Fokus geht gleich ins Feld. */
    const adresseOeffnen = (modus: "link" | "bild") => {
        keeper.save();
        setAdresse({ modus, wert: "https://" });
    };

    /**
     * Adresse übernehmen. Ein Link nimmt markierten Text als Linktext („wie
     * in Notion"); ein Bild aus dem Netz wird nicht hochgeladen, sondern
     * bleibt Live-Verweis — nur `https://`. Was die Adresse nicht erfüllt,
     * sagt der Hinweis; das Menü bleibt offen.
     */
    const adresseUebernehmen = () => {
        if (!adresse || !feld) return;
        const wert = adresse.wert.trim();
        if (!wert || wert === "https://") {
            setAdresse(null);
            return;
        }
        if (adresse.modus === "link") {
            const ziel = urlAusText(wert) ?? (/^mailto:\S+$/i.test(wert) ? wert : null);
            if (!ziel) {
                void hinweisen({ titel: "Adresse unvollständig", text: "Bitte eine vollständige Adresse eingeben, z. B. https://www.beispiel.de", ton: "danger" });
                return;
            }
            setAdresse(null);
            kommando(() => {
                feld.focus();
                linkEinsetzen(feld, aktiveAuswahl(), ziel);
            });
            return;
        }
        const img = externesBild(wert);
        if (!img) {
            void hinweisen({ titel: "Bild nicht einbettbar", text: "Nur eine https://-Adresse lässt sich als Bild einbetten.", ton: "danger" });
            return;
        }
        setAdresse(null);
        kommando(() => {
            feld.focus();
            einsetzenAnAuswahl(feld, aktiveAuswahl(), img);
        });
    };

    /** Dateidialog — die Auswahl vorher merken, der Dialog nimmt den Fokus. */
    const bildWaehlen = () => {
        keeper.save();
        dateiRef.current?.click();
    };

    const bildGewaehlt = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !onBildDatei) return;
        // `kommando` holt die Auswahl zurück — dorthin setzt der Block den
        // Platzhalter, bevor der Upload läuft.
        kommando(() => onBildDatei(file));
    };

    // Weder Leiste noch Aktionen: ein leerer Kasten wäre schlimmer als kein
    // Menü (dieselbe Vorsichtsregel wie in `Vg3Kontextmenue`).
    if (!feld && aktionen.length === 0) return null;

    return createPortal(
        <div
            ref={ref}
            className="kvp-rd nb-menue"
            role="menu"
            style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 3000 }}
        >
            {feld ? (
                <div className="nb-menue-format">
                    <div className="nb-fmt-zeile">
                        <select
                            className="nb-fmt-select"
                            aria-label="Schriftart"
                            value={PLATZHALTER}
                            onMouseDown={() => keeper.save()}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === PLATZHALTER) return;
                                klasse(v || null, GRUPPE_SCHRIFT);
                            }}
                        >
                            <option value={PLATZHALTER} disabled hidden>
                                Schriftart
                            </option>
                            <option value="">Standard</option>
                            {BLOCK_SCHRIFTEN.map((s) => (
                                <option
                                    key={s.klasse}
                                    value={s.klasse}
                                    style={{ fontFamily: s.stapel }}
                                >
                                    {s.label}
                                </option>
                            ))}
                        </select>
                        <select
                            className="nb-fmt-select nb-fmt-select-kurz"
                            aria-label="Schriftgröße"
                            value={PLATZHALTER}
                            onMouseDown={() => keeper.save()}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === PLATZHALTER) return;
                                klasse(v || null, GRUPPE_GROESSE);
                            }}
                        >
                            <option value={PLATZHALTER} disabled hidden>
                                Größe
                            </option>
                            <option value="">Standard</option>
                            {BLOCK_GROESSEN.map((g) => (
                                <option key={g.klasse} value={g.klasse}>
                                    {g.pt}
                                </option>
                            ))}
                        </select>
                        <Knopf
                            titel="Formatierung entfernen"
                            onClick={() => kommando(() => entferneFormatierung(feld))}
                        >
                            <OhneFormatZeichen />
                        </Knopf>
                        <Knopf
                            titel="Rückgängig (Strg+Z)"
                            gesperrt={!kannRueckgaengig(feld)}
                            onClick={zurueck}
                        >
                            <ZurueckZeichen />
                        </Knopf>
                    </div>

                    <div className="nb-fmt-zeile">
                        <Knopf titel="Fett" onClick={() => kommando(() => execCmd(feld, "bold"))}>
                            <b>F</b>
                        </Knopf>
                        <Knopf
                            titel="Kursiv"
                            onClick={() => kommando(() => execCmd(feld, "italic"))}
                        >
                            <i>K</i>
                        </Knopf>
                        <Knopf
                            titel="Unterstrichen"
                            onClick={() => kommando(() => execCmd(feld, "underline"))}
                        >
                            <u>U</u>
                        </Knopf>
                        <Knopf
                            titel="Durchgestrichen"
                            onClick={() => kommando(() => execCmd(feld, "strikeThrough"))}
                        >
                            <s>S</s>
                        </Knopf>
                        <span className="nb-fmt-trenner" aria-hidden />
                        <Knopf
                            titel="Aufzählung"
                            onClick={() => kommando(() => execCmd(feld, "insertUnorderedList"))}
                        >
                            <ListenZeichen />
                        </Knopf>
                        <Knopf
                            titel="Nummerierung"
                            onClick={() => kommando(() => execCmd(feld, "insertOrderedList"))}
                        >
                            <ListenZeichen nummeriert />
                        </Knopf>
                        <span className="nb-fmt-trenner" aria-hidden />
                        <Knopf titel="Link…" onClick={() => adresseOeffnen("link")}>
                            <LinkZeichen />
                        </Knopf>
                        {onBildDatei ? (
                            <Knopf titel="Bild…" onClick={bildWaehlen}>
                                <BildZeichen />
                            </Knopf>
                        ) : null}
                        <Knopf titel="Bild-URL…" onClick={() => adresseOeffnen("bild")}>
                            <BildUrlZeichen />
                        </Knopf>
                    </div>

                    {adresse ? (
                        <div className="nb-fmt-zeile nb-fmt-adresse">
                            <input
                                className="nb-fmt-url"
                                type="url"
                                aria-label={adresse.modus === "link" ? "Link-Adresse" : "Bild-Adresse"}
                                placeholder={
                                    adresse.modus === "link" ? "https://… oder mailto:…" : "https://…/bild.png"
                                }
                                value={adresse.wert}
                                autoFocus
                                onChange={(e) => setAdresse({ modus: adresse.modus, wert: e.target.value })}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        adresseUebernehmen();
                                    }
                                }}
                            />
                            <Knopf titel="Einfügen" onClick={adresseUebernehmen}>
                                Einfügen
                            </Knopf>
                        </div>
                    ) : null}
                    {onBildDatei ? (
                        <input
                            ref={dateiRef}
                            type="file"
                            accept={NOTIZBILD_MIME.join(",")}
                            aria-label="Bilddatei"
                            style={{ display: "none" }}
                            onChange={bildGewaehlt}
                        />
                    ) : null}

                    <div className="nb-fmt-zeile">
                        <span className="nb-fmt-label">Marker</span>
                        {BLOCK_MARKER.map((m) => (
                            <Knopf
                                key={m.klasse}
                                titel={`Marker ${m.label}`}
                                stil={{ background: m.hex }}
                                onClick={() => klasse(m.klasse, GRUPPE_MARKER, "mark")}
                            >
                                <span className="nb-fmt-tupfer" aria-hidden />
                            </Knopf>
                        ))}
                        <Knopf titel="Kein Marker" onClick={() => klasse(null, GRUPPE_MARKER, "mark")}>
                            <OhneZeichen />
                        </Knopf>
                    </div>

                    <div className="nb-fmt-zeile">
                        <span className="nb-fmt-label">Farbe</span>
                        {BLOCK_FARBEN.map((f) => (
                            <Knopf
                                key={f.klasse}
                                titel={`Schriftfarbe ${f.label}`}
                                stil={{ color: f.hex }}
                                onClick={() => klasse(f.klasse, GRUPPE_FARBE)}
                            >
                                <b>A</b>
                            </Knopf>
                        ))}
                        <Knopf titel="Standardfarbe" onClick={() => klasse(null, GRUPPE_FARBE)}>
                            <OhneZeichen />
                        </Knopf>
                    </div>
                </div>
            ) : null}

            {aktionen.length ? (
                <div className="nb-menue-aktionen">
                    {aktionen.map((a, i) => (
                        <React.Fragment key={`${i}-${a.label}`}>
                            {a.trennerDavor && <span className="nb-menue-trenner" aria-hidden />}
                            <button
                                type="button"
                                role="menuitem"
                                className={`nb-menue-item${a.gefahr ? " gefahr" : ""}`}
                                onClick={() => {
                                    onClose();
                                    a.onClick();
                                }}
                            >
                                {a.icon}
                                {a.label}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
            ) : null}
        </div>,
        document.body,
    );
};
